/* eslint-disable camelcase */
import React from 'react';
import PropTypes from 'prop-types';
import R from 'ramda';
import { isSmallBreakpoint } from '../../../utils';

// Sub-component for rendering images with optional title and link
const LookbookImage = ({ imageData }) => {
  if (!imageData || !imageData.url) return null;

  const { url, title, color_code, link } = imageData;

  return (
    <>
      <img src={url} alt={title} />
      {color_code && (
        <span
          className="lookbook__image__title"
          style={{ backgroundColor: color_code }}
        >
          {title}
        </span>
      )}
      {link && link.href && <a href={link.href}>{link.title}</a>}
    </>
  );
};

LookbookImage.propTypes = {
  imageData: PropTypes.shape({
    url: PropTypes.string.isRequired,
    title: PropTypes.string,
    color_code: PropTypes.string,
    link: PropTypes.shape({
      href: PropTypes.string,
      title: PropTypes.string,
    }),
  }),
};

// Sub-component for rendering individual products
const LookbookProduct = ({ product }) => {
  if (!product || product.length === 0) return null;

  const firstProduct = product[0];
  const { uid, locale, slug, title } = firstProduct;

  const categoryUrl = R.path(
    [0, 'product_subcategory', 0, 'category', 0, 'url'],
    product
  );
  const subcategoryUrl = R.path([0, 'product_subcategory', 0, 'url'], product);
  const productUrl = `/${locale}${categoryUrl || ''}${
    subcategoryUrl || ''
  }/${slug}`;

  const imageUrl = R.path([0, 'product_images', 0, 'image_xs'], product);
  const imageAlt = R.path([0, 'product_images', 0, 'alt_text'], product);

  return (
    <div key={uid} className="lookbook__product">
      <a href={productUrl}>
        <img src={imageUrl} alt={imageAlt || title} />
        <span>{title}</span>
      </a>
    </div>
  );
};

LookbookProduct.propTypes = {
  product: PropTypes.arrayOf(
    PropTypes.shape({
      uid: PropTypes.string.isRequired,
      locale: PropTypes.string,
      product_subcategory: PropTypes.arrayOf(
        PropTypes.shape({
          category: PropTypes.arrayOf(
            PropTypes.shape({
              url: PropTypes.string,
            })
          ),
          url: PropTypes.string,
        })
      ),
      slug: PropTypes.string,
      product_images: PropTypes.arrayOf(
        PropTypes.shape({
          image_xs: PropTypes.string,
          alt_text: PropTypes.string,
        })
      ),
      title: PropTypes.string,
    })
  ).isRequired,
};

const Lookbook = ({ lookbook, currentBreakpoint }) => {
  const imagesBlock = R.pathOr([], ['images_block'], lookbook);
  const isImagesBlock = imagesBlock.length === 9;

  // Extracting section1 images and products
  const section1Images = R.pathOr([], ['section', 'images'], lookbook);
  const section1Products = R.pathOr([], ['section', 'product'], lookbook);
  const section1HasMultipleImages = section1Images.length > 1;

  // Extracting section2 images and products
  const section2Images = R.pathOr([], ['section_2', 'images'], lookbook);

  const section2Products = R.pathOr([], ['section_2', 'product'], lookbook);
  const section2HasMultipleImages = section2Images.length > 1;

  const title = R.propOr('', 'title', lookbook);
  const description = R.propOr('', 'description', lookbook);

  const isSmall = isSmallBreakpoint({ currentBreakpoint });

  return (
    <div className="lookbook">
      <div className="grid-container">
        <div className="grid-x align-center">
          <div className="cell xsmall-16 medium-14">
            {/* Render main image for small breakpoints */}
            {isSmall &&
              (!isImagesBlock ? (
                <div className="lookbook__item item-1 lookbook__image">
                  <LookbookImage
                    imageData={{
                      url: R.path([0, 'image', 'url'], imagesBlock),
                      title: R.path([0, 'title'], imagesBlock),
                      color_code: R.path([0, 'color_code'], imagesBlock),
                      link: R.path([0, 'link'], imagesBlock),
                    }}
                  />
                </div>
              ) : (
                <div className="lookbook__grid-images lookbook__item item-1">
                  {imagesBlock.map((image, index) => (
                    <div
                      key={image.uid || index}
                      className="lookbook__item lookbook__image"
                    >
                      <LookbookImage
                        imageData={{
                          url: R.path(['image', 'url'], image),
                          title:
                            image.image_title ||
                            R.path(['image', 'title'], image),
                          color_code: image.color_code,
                          link: image.link,
                        }}
                      />
                    </div>
                  ))}
                </div>
              ))}
            <h3 className="lookbook__heading">{title}</h3>
            <div className="lookbook__description">{description}</div>
            <div className="lookbook__grid">
              {/* Render main image for non-small breakpoints */}
              {!isSmall &&
                (!isImagesBlock ? (
                  <div className="lookbook__item item-1 lookbook__image">
                    <LookbookImage
                      imageData={{
                        url: R.path([0, 'image', 'url'], imagesBlock),
                        title: R.path([0, 'title'], imagesBlock),
                        color_code: R.path([0, 'color_code'], imagesBlock),
                        link: R.path([0, 'link'], imagesBlock),
                      }}
                    />
                  </div>
                ) : (
                  <div className="lookbook__grid-images lookbook__item item-1">
                    {imagesBlock.map((image, index) => (
                      <div
                        key={image.uid || index}
                        className="lookbook__item lookbook__image"
                      >
                        <LookbookImage
                          imageData={{
                            url: R.path(['image', 'url'], image),
                            title:
                              image.image_title ||
                              R.path(['image', 'title'], image),
                            color_code: image.color_code,
                            link: image.link,
                          }}
                        />
                      </div>
                    ))}
                  </div>
                ))}

              {/* Section 1: Image */}
              {section1Images.length > 0 && (
                <div className="lookbook__item item-2 lookbook__image">
                  <LookbookImage
                    imageData={{
                      url: R.path([0, 'image', 'url'], section1Images),
                      title: R.path([0, 'image', 'title'], section1Images),
                      color_code: R.path([0, 'color_code'], section1Images),
                      link: R.path([0, 'link'], section1Images),
                    }}
                  />
                </div>
              )}

              {/* Section 1: Products or Second Image */}
              {section1HasMultipleImages
                ? section1Images.length > 1 && (
                    <div className="lookbook__item item-3 lookbook__image">
                      <LookbookImage
                        imageData={{
                          url: R.path([1, 'image', 'url'], section1Images),
                          title: R.path([1, 'image', 'title'], section1Images),
                          color_code: R.path([1, 'color_code'], section1Images),
                          link: R.path([1, 'link'], section1Images),
                        }}
                      />
                    </div>
                  )
                : section1Products.length > 0 && (
                    <div className="lookbook__item item-3">
                      {section1Products.map(({ product }) => (
                        <LookbookProduct
                          key={R.path([0, 'uid'], product)}
                          product={product}
                        />
                      ))}
                    </div>
                  )}

              {/* Section 2: Image */}
              {section2Images.length > 0 && (
                <div className="lookbook__item item-5 lookbook__image">
                  <LookbookImage
                    imageData={{
                      url: R.path([0, 'image', 'url'], section2Images),
                      title: R.path([0, 'image', 'title'], section2Images),
                      color_code: R.path([0, 'color_code'], section2Images),
                      link: R.path([0, 'link'], section2Images),
                    }}
                  />
                </div>
              )}

              {/* Section 2: Products or Second Image */}
              {section2HasMultipleImages
                ? section2Images.length > 1 && (
                    <div className="lookbook__item item-4 lookbook__image">
                      <LookbookImage
                        imageData={{
                          url: R.path([1, 'image', 'url'], section2Images),
                          title: R.path([1, 'image', 'title'], section2Images),
                          color_code: R.path([1, 'color_code'], section2Images),
                          link: R.path([1, 'link'], section2Images),
                        }}
                      />
                    </div>
                  )
                : section2Products.length > 0 && (
                    <div className="lookbook__item item-4">
                      {section2Products.map(({ product }) => (
                        <LookbookProduct
                          key={R.path([0, 'uid'], product)}
                          product={product}
                        />
                      ))}
                    </div>
                  )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

Lookbook.propTypes = {
  lookbook: PropTypes.shape({
    image: PropTypes.shape({
      image: PropTypes.shape({
        url: PropTypes.string,
        title: PropTypes.string,
      }),
      color_code: PropTypes.string,
      link: PropTypes.shape({
        href: PropTypes.string,
        title: PropTypes.string,
      }),
    }),
    section: PropTypes.shape({
      images: PropTypes.arrayOf(
        PropTypes.shape({
          image: PropTypes.shape({
            url: PropTypes.string,
            title: PropTypes.string,
          }),
          color_code: PropTypes.string,
          link: PropTypes.shape({
            href: PropTypes.string,
            title: PropTypes.string,
          }),
        })
      ),
      product: PropTypes.arrayOf(
        PropTypes.shape({
          product: PropTypes.array.isRequired,
        })
      ),
    }),
    section_2: PropTypes.shape({
      images: PropTypes.arrayOf(
        PropTypes.shape({
          image: PropTypes.shape({
            url: PropTypes.string,
            title: PropTypes.string,
          }),
          color_code: PropTypes.string,
          link: PropTypes.shape({
            href: PropTypes.string,
            title: PropTypes.string,
          }),
        })
      ),
      product: PropTypes.arrayOf(
        PropTypes.shape({
          product: PropTypes.array.isRequired,
        })
      ),
    }),
    title: PropTypes.string,
    description: PropTypes.string,
  }).isRequired,
  currentBreakpoint: PropTypes.string.isRequired,
};

export default Lookbook;
